import { useEffect, useState, type ChangeEvent } from "react";
import { Dialog } from "../../../../components/common/dialog";
import { Button } from "../../../../components/common/button/button";
import { Input } from "../../../../components/common/input";
import { ReviewChangeRequestDtoStatusEnum } from "../../../../api";

export type ReviewAction = ReviewChangeRequestDtoStatusEnum;

export interface ReviewNoteDialogProps {
  open: boolean;
  action: ReviewAction | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void | Promise<void>;
}

function isNoteRequired(action: ReviewAction | null): boolean {
  return (
    action === ReviewChangeRequestDtoStatusEnum.Rejected ||
    action === ReviewChangeRequestDtoStatusEnum.ChangesRequested
  );
}

function actionCopy(action: ReviewAction | null): {
  title: string;
  helper: string;
  confirmText: string;
} {
  switch (action) {
    case ReviewChangeRequestDtoStatusEnum.Approved:
      return {
        title: "Approve change request?",
        helper: "Proposed changes will be applied immediately. A note is optional.",
        confirmText: "Approve",
      };
    case ReviewChangeRequestDtoStatusEnum.Rejected:
      return {
        title: "Reject change request?",
        helper:
          "This is a terminal decision. The submitter cannot resubmit — a note is required so they know why.",
        confirmText: "Reject",
      };
    case ReviewChangeRequestDtoStatusEnum.ChangesRequested:
      return {
        title: "Request changes?",
        helper:
          "The submitter will be able to edit and resubmit. A note is required so they know what to fix.",
        confirmText: "Request changes",
      };
    default:
      return { title: "", helper: "", confirmText: "Confirm" };
  }
}

export function ReviewNoteDialog({
  open,
  action,
  isSubmitting = false,
  onClose,
  onConfirm,
}: ReviewNoteDialogProps) {
  const [note, setNote] = useState("");
  const required = isNoteRequired(action);
  const { title, helper, confirmText } = actionCopy(action);

  useEffect(() => {
    if (!open) setNote("");
  }, [open]);

  const trimmed = note.trim();
  const canConfirm =
    !isSubmitting && (!required || trimmed.length > 0);

  return (
    <Dialog
      open={open}
      setOpen={(next) => {
        if (!next) onClose();
      }}
      title={title}
      text={helper}
      showButtons={false}
      showCloseButton
      content={
        <div className="mt-2 flex flex-col gap-3">
          <Input
            label={`Review note ${required ? "(required)" : "(optional)"}`}
            multiline
            rows={4}
            value={note}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
            placeholder={
              required
                ? "Explain what needs to change..."
                : "Optional context for the submitter"
            }
            disabled={isSubmitting}
            required={required}
          />
          <div className="flex flex-col items-center gap-3 pt-2">
            <Button
              variant="primary"
              disabled={!canConfirm}
              onClick={async () => {
                await onConfirm(trimmed);
              }}
              className="min-w-[236px]"
            >
              {isSubmitting ? "Submitting..." : confirmText}
            </Button>
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={onClose}
              className="min-w-[236px]"
            >
              Cancel
            </Button>
          </div>
        </div>
      }
    />
  );
}
